/**
 * Кого показывать, когда участников больше, чем помещается.
 *
 * ── Зачем это нужно ─────────────────────────────────────────────────
 *
 * Каждая видимая плитка — это отдельный декодируемый видеопоток. На
 * современном ноутбуке один поток 360p стоит примерно 3–5% процессора,
 * и до десятка плиток браузер держит спокойно. Дальше начинается то,
 * что человек описывает как «всё тормозит»: падает частота кадров,
 * дёргается своя камера, звук начинает опаздывать. На встрече из сорока
 * человек сетка «показать всех» кладёт вкладку целиком.
 *
 * Есть и вторая, менее очевидная сторона. Пока плитка не отрисована,
 * `adaptiveStream` не подписывается на её поток вовсе — значит и сервер
 * его не пересылает. Ограничение здесь экономит не только процессор
 * зрителя, но и канал сервера, причём кратно: с сорока участниками
 * разница между «все видят всех» и «каждый видит двенадцать» — это
 * разница между 1560 потоками и 480.
 *
 * ── Почему плитки не скачут ─────────────────────────────────────────
 *
 * Самое простое решение — сортировать по «кто последний говорил» —
 * непригодно: на живом обсуждении плитки перетасовываются каждые
 * несколько секунд, и следить за этим невозможно.
 *
 * Поэтому порядок здесь стабильный. Он меняется в одном случае: кто-то
 * невидимый заговорил. Тогда он занимает место того из видимых, кто
 * молчит дольше всех, — одна замена вместо полной пересортировки.
 * Остальные плитки остаются на своих местах.
 */

export interface Placeable {
  sid: string;
  isLocal: boolean;
  isSpeaking: boolean;
  screenShareEnabled: boolean;
}

export interface SlotState {
  /** Кто сейчас на местах, в порядке показа. */
  order: string[];
  /** Когда участника последний раз слышали. */
  lastSpokeAt: Record<string, number>;
}

export const emptySlots = (): SlotState => ({ order: [], lastSpokeAt: {} });

/**
 * Сколько плиток показывать.
 *
 * Считается от ширины окна, а не от числа участников: узкий экран —
 * это почти всегда телефон, где и процессор слабее, и плитка размером
 * с почтовую марку всё равно не читается.
 */
export function tileLimit(width: number): number {
  if (width < 560) return 4;
  if (width < 900) return 6;
  if (width < 1400) return 9;
  return 12;
}

/**
 * Пересчитывает состав видимых плиток.
 *
 * Возвращает новое состояние. Вызывающий сравнивает `order` с прежним:
 * если состав не изменился, перерисовывать нечего.
 */
export function placeTiles(
  participants: Placeable[],
  state: SlotState,
  limit: number,
  now = Date.now(),
): SlotState {
  const present = new Map(participants.map((p) => [p.sid, p]));

  // Отметки времени храним только для тех, кто ещё во встрече, — иначе
  // за долгую встречу словарь распухнет именами всех, кто заходил.
  const lastSpokeAt: Record<string, number> = {};
  for (const p of participants) {
    lastSpokeAt[p.sid] = p.isSpeaking ? now : (state.lastSpokeAt[p.sid] ?? 0);
  }

  /*
   * Свои и показывающие экран — вне очереди и вне замен.
   *
   * Свою камеру человек ожидает видеть всегда, а демонстрация экрана —
   * это то, ради чего встречу обычно и собрали: вытеснить её чьим-то
   * лицом было бы прямой ошибкой.
   */
  const pinned = participants.filter((p) => p.isLocal || p.screenShareEnabled).map((p) => p.sid);
  const pinnedSet = new Set(pinned);

  // Прежний порядок без ушедших и без закреплённых.
  let order = state.order.filter((sid) => present.has(sid) && !pinnedSet.has(sid));

  const free = Math.max(0, limit - pinned.length);

  /*
   * Очередь ведём полную, а не только по видимым местам.
   *
   * Показываются первые `free`, остальные ждут своей очереди — но
   * положение в ней у них есть. Без этого «заговоривший из невидимых»
   * было бы некому заметить: список просто обрывался бы на пределе.
   */
  const seen = new Set(order);
  for (const p of participants) {
    if (pinnedSet.has(p.sid) || seen.has(p.sid)) continue;
    order.push(p.sid);
    seen.add(p.sid);
  }

  const visible = order.slice(0, free);
  const hidden = order.slice(free);

  /*
   * Замена: заговоривший из невидимых меняется местами с тем из видимых,
   * кто молчит дольше всех. Одна замена за пересчёт — этого достаточно,
   * чтобы говорящий появился в течение секунды, и мало, чтобы сетка
   * начала мельтешить.
   */
  const speakingHidden = hidden.find((sid) => present.get(sid)?.isSpeaking);

  if (speakingHidden && visible.length > 0) {
    let quietest = visible[0];
    for (const sid of visible) {
      if (present.get(sid)?.isSpeaking) continue;
      if ((lastSpokeAt[sid] ?? 0) < (lastSpokeAt[quietest] ?? 0)) quietest = sid;
    }

    // Меняем, только если вытесняемый сейчас молчит: иначе один
    // говорящий выталкивал бы другого посреди фразы.
    if (!present.get(quietest)?.isSpeaking) {
      const a = order.indexOf(quietest);
      const b = order.indexOf(speakingHidden);
      order = [...order];
      order[a] = speakingHidden;
      order[b] = quietest;
    }
  }

  return { order: [...pinned, ...order], lastSpokeAt };
}

/** Делит участников на показанных и скрытых, сохраняя порядок мест. */
export function splitByOrder<T extends { sid: string }>(
  participants: T[],
  order: string[],
  limit: number,
): { visible: T[]; hiddenCount: number } {
  const index = new Map(participants.map((p) => [p.sid, p]));
  const visible: T[] = [];

  for (const sid of order) {
    if (visible.length >= limit) break;
    const p = index.get(sid);
    if (p) visible.push(p);
  }

  return { visible, hiddenCount: Math.max(0, participants.length - visible.length) };
}
